import { TYPES } from "@/types";
import { myContainer } from "@/inversify.config";
import { IEmploymentService } from "@/interfaces/IEmploymentService";
import {
  ApplicantInformation,
  ApplicantNotes,
  ApplicantPositionResponse,
  ApplicantRequirement,
  DynamicProperty,
  LabelResponse,
  Skill,
} from "@/models/dtos";
import { ToastNotification } from "@/models/ToastNotification";
import { FormItemModel } from "@/models/CheckboxGroup";
import { FormBuilder2 } from "@/models/FormBuilder2";
import { ApplicantUser } from "@/models/ApplicantUser";
import { Container } from "inversify";
import { EditableSkill } from "../../models/dtos";
import GeneratePosition from "@/features/shared/models/GeneratePosition";

const client = myContainer.get<IEmploymentService>(TYPES.IEmploymentService);

export default {
  state: {
    applicant: null as ApplicantPositionResponse,
    applicantQuestionaire: null as Skill[],
    applicantNotes: null as ApplicantNotes[],
    applicantStatus: null as LabelResponse,
    applicantFormBuilder: null as FormBuilder2,
    applicantApplicationForm: null as FormItemModel[],
    applicantRealtion: null as ApplicantPositionResponse,
  },

  getters: {
    applicant(state: any) {
      return state.applicant;
    },
    applicantRelation(state: any) {
      return state.applicantRealtion;
    },
    applicantQuestionaire(state: any) {
      return state.applicantQuestionaire;
    },
    applicantNotes(state: any) {
      return state.applicantNotes;
    },
    applicantStatus(state: any) {
      return state.applicant?.labels?.find(
        (label: LabelResponse) => label?.labelType?.name === "Interest"
      ) as LabelResponse;
    },
    applicantProcess(state: any) {
      return state.applicant?.labels?.find(
        (label: LabelResponse) => label?.labelType?.name === "Process"
      ) as LabelResponse;
    },
    applicantInformation(state: any) {
      const children = [] as string[];
      let applicantInformation: ApplicantInformation[] = [];

      const filteredMetadataProperties = state.applicant?.metadata?.properties
        .filter(
          (px: DynamicProperty) =>
            px.key !== "form_file" && px.key !== "/position/images/bannerImage"
        )
        .map((px: DynamicProperty) => {
          let data;
          if (px.type === "group") {
            data = px.data.keys.map((key: string) => {
              const child = state.applicant?.metadata?.properties.find(
                (px: DynamicProperty) => px.key === key
              );
              children.push(key);
              return {
                key: child.key,
                label: child.label,
                value: child.data,
              } as ApplicantInformation;
            });
          } else {
            data = px.data;
          }

          return {
            key: px.key,
            label: px.label,
            type: px.type === "group" ? "checkbox" : "text",
            value: data,
            visible: !children.includes(px.key),
          } as ApplicantInformation;
        });

      const filteredWithoutChildren = filteredMetadataProperties.filter(
        (px: ApplicantInformation) => !children.includes(px.key)
      );

      const filteredApplicantInfo = Object.keys(state.applicant?.info)
        .filter((key) => key === "age" || key === "gender")
        .map((key) => {
          return {
            key: key,
            label: key === "age" ? "Ålder" : "Kön",
            type: "text",
            value: state.applicant?.info[key],
            visible: true,
          } as ApplicantInformation;
        });
      applicantInformation = [
        ...filteredWithoutChildren,
        ...filteredApplicantInfo,
      ];

      return applicantInformation;
    },
    getApplicantFormBuilder(state: any) {
      return state.applicantFormBuilder;
    },
    getApplicantApplicationForm(state: any) {
      return state.applicantApplicationForm;
    },

    getApplicantRequirementFullfillment(state: any) {
      const propertyKeys = state.applicant?.metadata?.properties
        .filter((prop: DynamicProperty) =>
          prop.key.includes("requirementsform")
        )
        .flatMap((group: any) => group.data.keys)
        .filter((key: string) => typeof key != typeof undefined);

      const properties = state.applicant?.metadata?.properties.filter(
        (px: DynamicProperty) => propertyKeys.includes(px.key)
      );

      const model = properties.map((x: DynamicProperty) => {
        const req = new ApplicantRequirement();
        (req.fullfilled = x.data), (req.key = x.key), (req.title = x.label);
        return req;
      });
      return model;
    },
    getApplicantSummerPositions(state: any) {
      let allSummerPositions = null as DynamicProperty[];

      const allSummerPositionsKeys =
        state.applicant?.metadata?.properties?.find((item: DynamicProperty) =>
          item.key.includes("summerpositionchoicesform")
        );
      if (allSummerPositionsKeys) {
        allSummerPositionsKeys.data.keys.forEach((item: string) => {
          const uniqueSummerPosition =
            state.applicant?.metadata?.properties?.find(
              (position: DynamicProperty) => position.key === item
            );
          if (uniqueSummerPosition) {
            if (allSummerPositions) {
              allSummerPositions.push(uniqueSummerPosition);
            } else {
              allSummerPositions = [uniqueSummerPosition];
            }
          }
        });
      }
      return allSummerPositions;
    },
    getApplicantPeriods(state: any) {
      let allPeriods = null as DynamicProperty[];

      const allPeriodsKeys = state.applicant?.metadata?.properties?.find(
        (item: DynamicProperty) => item.key.includes("summerperiodsform")
      );
      if (allPeriodsKeys) {
        allPeriodsKeys.data.keys.forEach((item: string) => {
          const uniquePeriod = state.applicant?.metadata?.properties?.find(
            (position: DynamicProperty) => position.key === item
          );
          if (uniquePeriod) {
            if (allPeriods) {
              allPeriods.push(uniquePeriod);
            } else {
              allPeriods = [uniquePeriod];
            }
          }
        });
      }
      return allPeriods;
    },
    getApplicantEmailSentStatus(state: any) {
      return state.applicant?.info?.emailNotificationSent ?? false;
    },
    getApplicantManualDriverLicenseStatus(state: any) {
      return state.applicant?.metadata?.properties?.find(
        (item: DynamicProperty) => item.key === "driverlicenseform_1"
      )?.data;
    },
    getApplicantAutomaticDriverLicenseStatus(state: any) {
      return state.applicant?.metadata?.properties?.find(
        (item: DynamicProperty) => item.key === "driverlicenseform_3"
      )?.data;
    },
    getApplicantOwnCarStatus(state: any) {
      return state.applicant?.metadata?.properties?.find(
        (item: DynamicProperty) => item.key === "driverlicenseform_2"
      )?.data;
    },
  },

  mutations: {
    setApplicant(state: any, item: ApplicantPositionResponse) {
      state.applicant = item;

      if (!state.applicantRealtion) {
        state.applicantRealtion = item;
      }
      const prev = state.applicantRealtion as ApplicantPositionResponse;

      const prevLabel = prev.labels.find(
        (x) => x.labelType.name === "Interest"
      );
      const currentLabel = item.labels.find(
        (x) => x.labelType.name === "Interest"
      );
      if (prevLabel.id === currentLabel.id) state.applicantRealtion = item;
    },
    setApplicantRealation(state: any, item: ApplicantPositionResponse) {
      state.applicantRealtion = item;
    },
    setApplicantQuestionaire(state: any, value: Skill[]) {
      state.applicantQuestionaire = value;
    },
    setApplicantNotes(state: any, value: ApplicantNotes[]) {
      state.applicantNotes = value;
    },
    setApplicantFormBuilder(state: any, value: FormItemModel[]) {
      state.applicantFormBuilder = value;
    },
    setApplicantApplicationForm(state: any, value: FormItemModel[]) {
      state.applicantApplicationForm = value;
    },
    setApplicantMedflyttare(state: any, value: any) {
      if (!state.applicant) return;
      state.applicant.isMedflyttare = value.isMedflyttare;
      state.applicant.medflyttare = value.isMedflyttare
        ? value.medflyttare
        : undefined;
    },
  },
  actions: {
    async createDummyApplicant(
      { getters, commit }: any,
      item: GeneratePosition
    ) {
      await client.JobPosition.createDemoApplicants(item);
    },
    async setCurrentApplicantMedflyttare(
      { commit }: any,
      { applicantPositionId, isMedflyttare, status, note, relocationDate }: any
    ) {
      await client.JobPosition.setApplicantMedflyttare(
        applicantPositionId,
        isMedflyttare,
        status,
        note,
        relocationDate
      );
      commit("setApplicantMedflyttare", {
        isMedflyttare: isMedflyttare,
        medflyttare: {
          isActive: isMedflyttare,
          status: status,
          note: note,
          relocationDate: relocationDate,
        },
      });
    },
    async loadApplicantById({ getters, commit }: any, applicantId: number) {
      try {
        const response = (await client.JobPosition.getApplicantPosition(
          applicantId
        )) as ApplicantPositionResponse;
        commit("setApplicant", response);

        const applicantItems = new FormBuilder2(response.metadata, false);
        commit("setApplicantFormBuilder", applicantItems);
        commit("setApplicantApplicationForm", applicantItems.Items);

        // Loggar meriterna här
      } catch (error) {
        // Hantera fel här
        console.error("Ett fel uppstod vid hämtning av ansökan:", error);
      }
    },
    async resetApplicantRelation({ commit }: any) {
      commit("setApplicantRealation", null);
    },
    async loadApplicantQuestionaire(
      { commit, getters }: any,
      applicantId: number
    ) {
      const payload = {
        applicantId: applicantId,
        containerId: getters.container.id,
      };
      const response = await client.JobPosition.getApplicantQuestionaire(
        payload
      );
      commit("setApplicantQuestionaire", response);
    },
    async updateApplicantQuestionaire(
      { dispatch, getters }: any,
      {
        applicantId,
        skills,
      }: {
        applicantId: number;
        skills: Skill[];
      }
    ) {
      const payload = {
        applicantId: applicantId,
        containerId: getters.container.id,
        skills: skills,
      };
      const response = await client.JobPosition.updateApplicantQuestionaire(
        payload
      );
      await dispatch("loadApplicantQuestionaire", applicantId);

      return response;
    },
    updateAnswer(
      { state }: any,
      {
        questionId,
        answer,
      }: {
        questionId: string;
        answer: string;
      }
    ) {
      state.applicantQuestionaire.forEach((question: Skill) => {
        if (question.questions.find((q) => q.id === questionId)) {
          question.questions.find((q) => q.id === questionId).answer = answer;
        }
      });
    },
    async addApplicantNote({ dispatch, commit }: any, { id, note }: any) {
      try {
        const response = await client.JobPosition.createApplicantNote(id, note);

        dispatch("loadApplicantById", id);

        dispatch(
          "addNotification",
          new ToastNotification("success", "Anteckning sparad")
        );
        commit("setApplicantNotes", response);
      } catch (error) {
        dispatch(
          "addNotification",
          new ToastNotification("error", "Något gick fel")
        );
      }
    },
    async setApplicantLabel(
      { dispatch, commit, getters }: any,
      { labelId, ids }: any
    ) {
      try {
        commit("setApplicantRealation", getters.applicant);
        await client.JobPosition.updateJobApplicationStatus(labelId, ids);
        dispatch("loadApplicantById", ids[0]);
      } catch (error) {
        dispatch(
          "addNotification",
          new ToastNotification("alert", "Något gick fel, vänligen försök igen")
        );
      }
    },
    async setPresentedToClient(
      { dispatch }: any,
      { applicantPositionId, presented }: any
    ) {
      try {
        await client.JobPosition.setPresentedToClient(
          applicantPositionId,
          presented
        );
        dispatch("loadApplicantById", applicantPositionId);
      } catch (error) {
        dispatch(
          "addNotification",
          new ToastNotification("alert", "Något gick fel, vänligen försök igen")
        );
      }
    },
  },
};
